/**
 * `tokenSource` — a module-level, React-free registry for the SPA's
 * outbound `Authorization` header value.
 *
 * Why this exists (auth-header migration #773):
 *   `api/client.ts` `apiFetch` is deliberately importable from
 *   non-React contexts (Zustand stores, the SSE solve stream), so it
 *   cannot call the `useAuth()` hook to mint a token. `AuthContext`
 *   registers a single async getter here on mount; `apiFetch` reads it
 *   and fills the `Authorization` header itself. This is now the only
 *   auth path — call-sites no longer thread their own header.
 *
 *   `apiFetch` only reads the source when the caller did NOT already set
 *   `Authorization`, so the source fills auth for essentially every
 *   request. The sole remaining explicit-header caller is AuthContext's
 *   own `/api/account/me` dev-shim bootstrap.
 *
 * The registered getter returns the FULL header value — e.g.
 *   `Bearer <clerk-jwt>` (Clerk mode) or `Bearer dev:<accountId>`
 *   (dev-shim) — or `null` when there is no active session.
 */

let _getAuthHeaderValue: (() => Promise<string | null>) | null = null;

/**
 * Register (or clear, with `null`) the module-level token getter.
 *
 * `AuthContext` calls this on mount and whenever the backing identity
 * changes (the dev-shim accountId, or the Clerk signed-in state /
 * `getToken`), and clears it (`setTokenSource(null)`) on unmount so a
 * stale closure can never leak across providers.
 */
export function setTokenSource(fn: (() => Promise<string | null>) | null): void {
  _getAuthHeaderValue = fn;
}

/**
 * Resolve the current `Authorization` header value, or `null`.
 *
 * Defensive by contract: returns `null` — never throws — when no
 * source is registered, when the source resolves to `null`, or when
 * the source itself rejects/throws. A token-mint failure must not
 * break an outbound fetch in a way that differs from today's
 * behaviour (today a missing token simply means no header).
 */
export async function getAuthHeaderValue(): Promise<string | null> {
  const fn = _getAuthHeaderValue;
  if (!fn) return null;
  try {
    return await fn();
  } catch {
    return null;
  }
}
